import { Fade, Stack } from '@mui/material';
import Skeleton from '@watershed/ui-core/components/Skeleton';
import { CommandLoadingSkeletonProps } from './types';

/**
 * Header skeleton component for command palette
 */
function HeaderSkeleton() {
  return (
    <Stack
      sx={{
        p: 1,
        width: '100%',
        borderBottom: (theme) => `0.5px solid ${theme.palette.grey20}`,
      }}
    >
      <Skeleton
        width="40%"
        height={28}
        sx={{
          borderRadius: 0.5,
          color: (theme) => theme.palette.grey20,
        }}
      />
      <Skeleton
        width="60%"
        height={32}
        sx={{
          borderRadius: 0.5,
          color: (theme) => theme.palette.grey20,
        }}
      />
    </Stack>
  );
}

/**
 * Content skeleton component for command palette
 */
function ContentSkeleton({ type }: { type: 'list' | 'detail' }) {
  return (
    <Stack gap={1} sx={{ px: 1.5 }} direction="row" alignItems="center">
      <Skeleton
        width={28}
        height={28}
        variant="circular"
        sx={{ color: (theme) => theme.palette.grey20 }}
      />
      <Skeleton
        width={type === 'list' ? '60%' : '80%'}
        height={34}
        sx={{
          borderRadius: 0.5,
          color: (theme) => theme.palette.grey20,
        }}
      />
    </Stack>
  );
}

/**
 * Footer skeleton component for command palette
 */
function FooterSkeleton() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        p: 1,
        width: '100%',
        borderTop: (theme) => `0.5px solid ${theme.palette.grey20}`,
      }}
    >
      <Skeleton
        width="56px"
        height={28}
        sx={{
          borderRadius: 0.5,
          color: (theme) => theme.palette.grey20,
        }}
      />
      <Skeleton
        width="56px"
        height={28}
        sx={{
          borderRadius: 0.5,
          color: (theme) => theme.palette.grey20,
        }}
      />
    </Stack>
  );
}

/**
 * Loading skeleton component for command palette
 * Displays a loading state with customizable sections
 */
export default function CommandLoadingSkeleton({
  type,
  showHeader = true,
  showFooter = true,
  isLoading = true,
}: CommandLoadingSkeletonProps) {
  return (
    <Stack height="100%" gap={1} sx={{ opacity: 0.5 }}>
      <Fade in={isLoading} timeout={300} unmountOnExit>
        <Stack>
          {showHeader && <HeaderSkeleton />}
          <ContentSkeleton type={type} />
          {showFooter && <FooterSkeleton />}
        </Stack>
      </Fade>
    </Stack>
  );
}
